import { useNavigate, useSearchParams } from 'react-router';
import type { DailyRewardStatusDto, MissionDto } from '@cardhub/protocol';
import { api } from '../api/client';
import { Coins, Confetti, ErrorState, Loading, Progress, Segmented, TopBar } from '../components/ui';
import { useFormat, useT } from '../i18n';
import { errorMessage, useAsync } from '../lib/hooks';
import { sfx } from '../lib/sound';
import { toast } from '../store/ui';
import { useState } from 'react';

interface Achievement {
  code: string;
  name: string;
  description: string;
  icon: string;
  reward: { coins: number; xp: number };
  unlockedAt: string | null;
}

export function Rewards() {
  const t = useT();
  const f = useFormat();
  const nav = useNavigate();
  const [search, setSearch] = useSearchParams();
  const tab = (search.get('tab') ?? 'daily') as 'daily' | 'missions' | 'achievements';
  const daily = useAsync(() => api.get<DailyRewardStatusDto>('/api/v1/rewards/daily'), []);
  const missions = useAsync(() => api.get<MissionDto[]>('/api/v1/missions'), []);
  const achievements = useAsync(() => api.get<Achievement[]>('/api/v1/achievements'), []);
  const [celebrate, setCelebrate] = useState(false);

  const claimDaily = async () => {
    try {
      const r = await api.post<{ coins: number; xp: number }>('/api/v1/rewards/daily/claim');
      sfx.win();
      setCelebrate(true);
      setTimeout(() => setCelebrate(false), 2500);
      toast(`+${r.coins} coins · +${r.xp} XP`, 'success');
      daily.reload();
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };
  const claimMission = async (m: MissionDto) => {
    try {
      const r = await api.post<{ coins: number }>(`/api/v1/missions/${m.id}/claim`);
      sfx.coin();
      toast(`+${r.coins} coins`, 'success');
      missions.reload();
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };

  return (
    <main className="page">
      {celebrate && <Confetti />}
      <TopBar title={t('lobby.daily')} onBack={() => nav(-1)} />
      <Segmented
        label="Rewards"
        value={tab}
        onChange={(v) => setSearch({ tab: v }, { replace: true })}
        options={[
          { value: 'daily', label: t('daily.title') },
          { value: 'missions', label: t('missions.title') },
          { value: 'achievements', label: t('profile.achievements') },
        ]}
      />
      <div style={{ marginTop: 16 }}>
        {tab === 'daily' &&
          (daily.loading ? (
            <Loading />
          ) : daily.error ? (
            <ErrorState message={daily.error} onRetry={daily.reload} />
          ) : (
            <>
              <div className="quick-row" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))' }}>
                {daily.data!.calendar.map((d) => (
                  <div key={d.day} className="quick-tile" style={{ borderColor: d.today ? 'var(--accent)' : undefined, opacity: d.claimed && !d.today ? 0.55 : 1, boxShadow: d.today && daily.data!.canClaim ? 'var(--shadow-glow)' : undefined }}>
                    <span>{t('daily.day', { n: d.day })}</span>
                    <span className="ico">{d.claimed ? '✅' : d.day === 7 ? '🎁' : '🪙'}</span>
                    <Coins amount={d.coins} size={14} />
                  </div>
                ))}
              </div>
              <button className="btn primary lg block" style={{ marginTop: 16 }} disabled={!daily.data!.canClaim} onClick={() => void claimDaily()}>
                {daily.data!.canClaim ? t('daily.claim') : `${t('daily.comeBack')}${daily.data!.nextClaimAt ? ` · ${f.relative(daily.data!.nextClaimAt)}` : ''}`}
              </button>
            </>
          ))}
        {tab === 'missions' &&
          (missions.loading ? (
            <Loading />
          ) : missions.error ? (
            <ErrorState message={missions.error} onRetry={missions.reload} />
          ) : (
            (['DAILY', 'WEEKLY', 'EVENT'] as const).map((period) => {
              const list = missions.data!.filter((m) => m.period === period);
              if (list.length === 0) return null;
              return (
                <section key={period}>
                  <h2 className="section-title">
                    {period === 'DAILY' ? t('missions.daily') : period === 'WEEKLY' ? t('missions.weekly') : t('lobby.events')}
                    <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{t('missions.resets', { time: f.relative(list[0]!.expiresAt) })}</span>
                  </h2>
                  <div className="list">
                    {list.map((m) => (
                      <div key={m.id} className="list-item">
                        <div className="grow col" style={{ gap: 6 }}>
                          <div className="spread">
                            <strong>{m.name}</strong>
                            <span className="num muted" style={{ fontSize: 'var(--fs-sm)' }}>{m.progress}/{m.target}</span>
                          </div>
                          <Progress value={m.progress} max={m.target} label={m.name} />
                          <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{m.description}</span>
                        </div>
                        {m.status === 'CLAIMED' ? (
                          <span className="pill">{t('common.claimed')}</span>
                        ) : (
                          <button className="btn sm primary" disabled={m.status !== 'COMPLETED'} onClick={() => void claimMission(m)}>
                            <Coins amount={m.reward.coins} size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              );
            })
          ))}
        {tab === 'achievements' &&
          (achievements.loading ? (
            <Loading />
          ) : (
            <div className="list">
              {achievements.data?.map((a) => (
                <div key={a.code} className="list-item" style={{ opacity: a.unlockedAt ? 1 : 0.6 }}>
                  <span style={{ fontSize: 28 }} aria-hidden>{a.unlockedAt ? '🏅' : '🔒'}</span>
                  <span className="grow">
                    <strong>{a.name}</strong>
                    <br />
                    <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{a.description}</span>
                  </span>
                  {a.reward.coins > 0 && <Coins amount={a.reward.coins} size={14} />}
                </div>
              ))}
            </div>
          ))}
      </div>
    </main>
  );
}
